import './record-mask-display.scss'

import _ from 'lodash'
import { get } from 'lodash'
import { Component } from 'vue-property-decorator'
import { faCaretDown, faCaretRight } from '@fortawesome/free-solid-svg-icons'

import { RecordMaskCategory, RecordMask, EntityField, getTranslatedString, EntityFieldStatus } from '@skinsoft/types'
import { MetaRecordMaskDataStore } from '@skinsoft/record-mask/src/meta-record-mask-data-store'
import { RecordMaskDataStore } from '@skinsoft/record-mask/src/record-mask-data-store'

import { ReactiveVue } from '@skinsoft/reactive-vue'
import { CollapsibleSection } from '@skinsoft/ui'
import { DELETE_IMAGE_CLICK_TOPIC, REPORT_EDITOR_CLOSE_ALL_TABS_TOPIC, REPORT_EDITOR_OPEN_ALL_TABS_TOPIC } from '@skinsoft/bus/src/busTopics'
import { AddMediaEvent, MediasFieldsArea } from '@skinsoft/entity-ui/src/media/medias-fields-area'
import { MaskField } from '../mask-field'

@Component({
    components: {
        sCollapsibleSection: CollapsibleSection,
        sMediasFieldsArea: MediasFieldsArea,
        sMaskField: MaskField,
    },
    props: ['recordMask', 'entity', 'isMetaMask', 'editing', 'getEntityFieldStatus'],
    template: require('./record-mask-display.html')
})
export class RecordMaskDisplay extends ReactiveVue {

    public entity: any;
    public isMetaMask: boolean;
    public recordMask: RecordMask;
    public editing: boolean

    public getEntityFieldStatus: (entityField: EntityField) => EntityFieldStatus

    // Icons
    public caretRightIcon = faCaretRight;
    public caretDownIcon = faCaretDown;


    private $recordMaskDataStore: RecordMaskDataStore;
    private $metaRecordMaskDataStore: MetaRecordMaskDataStore;

    private DELETE_IMAGE_CLICK_TOPIC = DELETE_IMAGE_CLICK_TOPIC

    public onCollapseAllClick() {
        this.emitOnBus(REPORT_EDITOR_CLOSE_ALL_TABS_TOPIC);
    }

    public onDeployAllClick() {
        this.emitOnBus(REPORT_EDITOR_OPEN_ALL_TABS_TOPIC)
    }

    private getCategoryLabel(category: RecordMaskCategory) {
        if (category.label) {
            const label = getTranslatedString(category.label, this.$i18n.locale)
            return label;
        }
        return ""
    }

    private updateValue(updateEvent) {
        this.$emit('value-change', { ...updateEvent, subWemi: this.getSubWemi(updateEvent.abstractEntity) })
    }

    /**
     * Return the name of the subfield of 'this.entity' corresponding
     * to the right wemi part for a given entityField
     * @param entityField 
     */
    private getSubWemi(abstractEntity: string) {
        switch (abstractEntity) {
            case "museumItem":
                return "item";
            case "museumManifestation":
                return "manifestation";
            case "work":
                return "work";
            case "expression":
                return "expression";
            default:
                return null
        }

    }

    private getValuePath(entityField: EntityField) {
        if(!(navigator.onLine) && entityField.pathOffline) {
            return entityField.pathOffline
        }

        return entityField.path
    }

    private getFieldValue(entityField: EntityField) {
        // Sometimes in offline mode, the item property of wemi contains also fields from other wemis entities
        // in this case, it is possible that the right value is inside the item and not the other wemi field.
        // To allow to get the right value in any case, we pass all possible values matching the entity field
        // path to children
        // Example: wemi: { item: { schema: { value: 'OK' } }, expression: { schema: { value: null } } }
        // the current entityField : { path: 'schema', entity: 'expression', entityFields: [...a child that match the value field] } 
        // we have to pass wemi.item.schema and wemi.expression.schema to the children so they are able to pick 
        // the correct value (because we don't no the children path so we cannot pick the right one here, as both 
        // have content)

        if (this.isMetaMask) {
            return this.getFieldValueSubWemi(entityField) ?? 
                this.getFieldValueItem(entityField) ?? 
                this.getFieldValueFromRoot(entityField)
        }
        else {
            return this.getFieldValueFromRoot(entityField)
        }
    }

    private getFieldValueSubWemi(entityField: EntityField) {
        // Sometimes in offline mode, the item property of wemi contains also fields from other wemis entities
        // in this case, it is possible that the right value is inside the item and not the other wemi field.
        // To allow to get the right value in any case, we pass all possible values matching the entity field
        // path to children
        // Example: wemi: { item: { schema: { value: 'OK' } }, expression: { schema: { value: null } } }
        // the current entityField : { path: 'schema', entity: 'expression', entityFields: [...a child that match the value field] } 
        // we have to pass wemi.item.schema and wemi.expression.schema to the children so they are able to pick 
        // the correct value (because we don't no the children path so we cannot pick the right one here, as both 
        // have content)

        if(this.isMetaMask) {
            let path = this.getValuePath(entityField)

            const subWemi = this.getSubWemi(entityField.abstractEntity!)
            const valueInSubWemi = get(this.entity, `${subWemi}.${path}`)

            return valueInSubWemi
        }

        return null
    }

    private getFieldValueItem(entityField: EntityField) {
        // Sometimes in offline mode, the item property of wemi contains also fields from other wemis entities
        // in this case, it is possible that the right value is inside the item and not the other wemi field.
        // To allow to get the right value in any case, we pass all possible values matching the entity field
        // path to children
        // Example: wemi: { item: { schema: { value: 'OK' } }, expression: { schema: { value: null } } }
        // the current entityField : { path: 'schema', entity: 'expression', entityFields: [...a child that match the value field] } 
        // we have to pass wemi.item.schema and wemi.expression.schema to the children so they are able to pick 
        // the correct value (because we don't no the children path so we cannot pick the right one here, as both 
        // have content)

        if(this.isMetaMask) {
            let path = this.getValuePath(entityField)

            const subWemi = this.getSubWemi('museumItem')
            const valueInSubWemi = get(this.entity, `${subWemi}.${path}`)

            return valueInSubWemi
        }

        return null
    }

    private getFieldValueFromRoot(entityField: EntityField) {
        // Sometimes in offline mode, the item property of wemi contains also fields from other wemis entities
        // in this case, it is possible that the right value is inside the item and not the other wemi field.
        // To allow to get the right value in any case, we pass all possible values matching the entity field
        // path to children
        // Example: wemi: { item: { schema: { value: 'OK' } }, expression: { schema: { value: null } } }
        // the current entityField : { path: 'schema', entity: 'expression', entityFields: [...a child that match the value field] } 
        // we have to pass wemi.item.schema and wemi.expression.schema to the children so they are able to pick 
        // the correct value (because we don't no the children path so we cannot pick the right one here, as both 
        // have content)

        let path = this.getValuePath(entityField)
        const value = get(this.entity, path)

        return value
    }

    public get associatedMediasFieldName() {
        return `associatedMedias`
    }

    public get itemForMediaArea(){
        if(this.entity.item){
            return this.entity.item;
        }
        return this.entity;
    }

    public onAddMedia(media: AddMediaEvent) {
        this.$emit('addMedia', media)
    }

    public onImageChoice(media) {
        this.$emit('imageChoice', media)
    }
}